import React from 'react';
import AsyncSelect from 'react-select/async';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@lomarengas/design-system';
import { useAppDispatch } from '../../../app/hooks';
import {
  AccommodationAutoCompleteOption,
  getAccommodationAutoCompleteOptionsAsync,
} from '../../../features/accommodations/accommodationsSlice';

interface CabinQuickSearchProps {
  NavigateUrl: string;
  Placeholder?: string;
  Subtitle: string;
  Title: string;
}

interface OptionType {
  value: string;
  label: string;
}

const CabinQuickSearch = ({
  NavigateUrl,
  Placeholder = 'Hae kohteen numerolla tai nimellä...',
  Subtitle,
  Title,
}: CabinQuickSearchProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleSelectAccommodationChange = (selectedOption: OptionType) => {
    if (selectedOption && selectedOption.label && selectedOption.value) {
      const accommodationUrlName = encodeURI(selectedOption.label);
      const accommodationId = selectedOption.value;
      navigate(
        `${NavigateUrl}/${accommodationId}?accommodation=${accommodationUrlName}`,
      );
    }
  };

  // Transform fetched options to match react-select format
  const formatOptions = (
    options: AccommodationAutoCompleteOption[] | undefined,
  ): OptionType[] => {
    if (!options || options.length === 0) {
      return [];
    }

    const newOptions = options.map(option => ({
      value: option.accommodationId,
      label: `${option.accommodationNumber} - ${option.name}`,
    }));
    return newOptions;
  };

  const loadOptions = async (inputValue: string): Promise<OptionType[]> => {
    try {
      const response = await dispatch(
        getAccommodationAutoCompleteOptionsAsync(inputValue),
      );
      // Access response data and format the options
      return formatOptions(response.payload.data);
    } catch (error) {
      console.error('Error fetching options:', error);
      return [];
    }
  };

  return (
    <div
      className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
      style={{ maxWidth: 640 }}
    >
      <div className="d-flex align-items-start align-items-sm-center mb-24 mb-xl-32">
        <div className="d-block bg-primary fs-3 me-24 px-16 py-8 rounded text-center text-white">
          <Icon Name="house-fill" />
        </div>
        <div className="flex-grow-1">
          <h2 className="h5 mb-0">{Title}</h2>
          <p className="text-gray-600 mb-0">{Subtitle}</p>
        </div>
      </div>
      <AsyncSelect
        styles={{
          control: baseStyles => ({
            ...baseStyles,
            borderColor: '#ced4da',
            borderRadius: 6,
          }),
          dropdownIndicator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
          indicatorSeparator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
        }}
        classNames={{
          control: state => 'form-control form-control-search ps-48 pe-8',
        }}
        noOptionsMessage={() => 'Ei tuloksia'}
        inputId="accommodationId"
        name="accommodationId"
        placeholder={Placeholder}
        isClearable
        isSearchable
        loadOptions={loadOptions}
        cacheOptions
        onChange={option => handleSelectAccommodationChange(option)}
      />
    </div>
  );
};

export default CabinQuickSearch;
export { CabinQuickSearch };
export type { CabinQuickSearchProps };
